// Client for the 2merative admin API on the PHP VPS (forms.2merative.com).
//
// Authentication happens on the server: the password is sent to login.php,
// verified against a server-side hash, and the server sets an HttpOnly session
// cookie. Nothing secret is stored in the browser. The localStorage
// "isAdmin" value is only a UI hint (show admin links, probe the session);
// it grants no access.

const API_BASE = (process.env.REACT_APP_ADMIN_API_BASE || "https://forms.2merative.com/api/admin").replace(/\/$/, "")
export const ADMIN_HINT_KEY = "isAdmin"

let csrfToken = ""

export class AdminApiError extends Error {
  constructor(status, message) {
    super(message)
    this.status = status
  }
}

async function request(path, { method = "GET", body } = {}) {
  const headers = { Accept: "application/json" }
  if (body !== undefined) headers["Content-Type"] = "application/json"
  if (method !== "GET" && csrfToken) headers["X-CSRF-Token"] = csrfToken

  let response
  try {
    response = await fetch(`${API_BASE}/${path}`, {
      method,
      headers,
      credentials: "include",
      cache: "no-store",
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch {
    throw new AdminApiError(0, "Unable to reach the admin service. Please try again.")
  }

  const data = await response.json().catch(() => null)
  if (!response.ok) {
    throw new AdminApiError(response.status, data?.error || "Request failed.")
  }
  return data
}

export function setAdminHint(on) {
  try {
    if (on) localStorage.setItem(ADMIN_HINT_KEY, "true")
    else localStorage.removeItem(ADMIN_HINT_KEY)
    window.dispatchEvent(new Event("storage"))
  } catch {
    // storage unavailable: the hint is cosmetic only
  }
}

export function hasAdminHint() {
  try {
    return localStorage.getItem(ADMIN_HINT_KEY) === "true"
  } catch {
    return false
  }
}

export async function login(password) {
  const data = await request("login.php", { method: "POST", body: { password } })
  csrfToken = data.csrfToken || ""
  setAdminHint(true)
  return data
}

export async function getSession() {
  const data = await request("session.php")
  csrfToken = data.authenticated ? data.csrfToken || "" : ""
  if (!data.authenticated) setAdminHint(false)
  return Boolean(data.authenticated)
}

export async function logout() {
  try {
    if (!csrfToken) await getSession()
    await request("logout.php", { method: "POST" })
  } finally {
    csrfToken = ""
    setAdminHint(false)
  }
}

export function getDocument(type, slug = "") {
  const params = new URLSearchParams({ type })
  if (slug) params.set("slug", slug)
  return request(`document.php?${params.toString()}`)
}
